import { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import axios from 'axios';
import MainLayout from "../layout/mainLayout";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


export default function DownloadLog() {

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const page = useRef(0);
    const search = useRef(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertText, setAlertText] = useState({
        msg: '',
        type: 'error'
    });
    const [expandedRow, setExpandedRow] = useState(null);
    const searchText = useRef('');
    const tableRef = useRef(null);

    const observer = useRef();
    const lastFileElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                page.current++;
                loadFiles();
            }
        })
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        loadFiles();
        document.title = `Download Log - ${process.env.REACT_APP_SITE_NAME}`;
    }, []);


    const loadFiles = (search = '') => {
        setLoading(true);
        axios({
            method: 'get',
            url: `https://downloadlog.goplugin.shop/downloadLogs?search=${searchText.current}&skip=${page.current}&take=20`,
            headers: {
                'x-api-key': 'CiFRQeO2607J64BrpOA4Kkv33ncc6s',
                'Content-Type': 'application/json'
            }
        }).then(function (response) {
            if (response.status === 200) {
                setData(prevData => [...prevData, ...response.data]);
                setHasMore(response.data.length > 0);
                setLoading(false);
            }
        }).catch(function (error) {
            setAlertText({
                msg: 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
                type: 'error'
            });
            setAlertOpen(true);
            setLoading(false);
        });
    }

    const submitSearch = (e) => {
        e.preventDefault();
        const inputValue = search.current.elements["top-search"].value;
        if (inputValue.trim() === "") {
            searchText.current = '';
            setData([]);
            page.current = 0;
            loadFiles();
            return;
        }
        searchText.current = inputValue;
        setData([]);
        page.current = 0;
        loadFiles(inputValue);
    }

    const convertFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    return (
        <>
            <div id="real-main" className='flex flex-col justify-between' ref={tableRef}>
                <div className='flex justify-between items-center mt-4'>
                    <h1 className='font-bold text-xl '>Download Log</h1>
                </div>
                <div className='table-responsive mt-2 bg-white'>
                    <table className="table-auto w-full border-collapse border border-slate-150">
                        <thead>
                            <tr>
                                <th className='text-left border-b border-slate-150 p-4 pl-8 pt-3 pb-3 font-bold text-noto'>IP Address</th>
                                <th className='text-left border-b border-slate-150 p-4 pl-8 pt-3 pb-3 font-bold text-noto'>Timestamp</th>
                                <th className='text-left border-b border-slate-150 p-4 pl-8 pt-3 pb-3 font-bold text-noto'>Filename</th>
                                <th className='text-right border-b border-slate-150 p-4 pl-8 pr-6 pt-3 pb-3 font-bold text-noto'></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length > 0 ? Object.entries(data).map(([k, item], index) => (
                                <>
                                    <tr
                                        onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                                        className={`align-center cursor-pointer ${expandedRow === index ? 'bg-slate-50 hidden' : 'border-b border-b-slate-150 hover:bg-slate-50'} `}
                                        key={k}
                                        ref={index === data.length - 1 ? lastFileElementRef : null}
                                    >
                                        <td className='p-4 py-1 pl-8'>
                                            <h4 className=''>{item.ip}</h4>
                                        </td>
                                        <td className='p-4 pl-8'>
                                            <p>{
                                                new Date(item.timestamp).toLocaleString('th-TH', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: 'numeric'
                                                })
                                            }</p>
                                        </td>
                                        <td className='p-4 pl-8'>
                                            <p>{item.filename}</p>
                                        </td>
                                        <td className='p-4 pl-8 text-right'>
                                            <div className='flex gap-2 justify-end'>
                                                <button onClick={() => setExpandedRow(expandedRow === index ? null : index)}>
                                                    {expandedRow === index ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedRow === index && (
                                        <tr className='border-b border-b-slate-150 bg-slate-50'>
                                            <td colSpan={4}>
                                                <div className='py-4 pl-8'>
                                                    <h4 className='font-bold'>IP Address</h4>
                                                    <p>{item.ip}</p>
                                                </div>
                                                <div className='pb-4 pl-8'>
                                                    <h4 className='font-bold'>User-Agent</h4>
                                                    <p>{item.userAgent}</p>
                                                </div>
                                                <div className='pb-4 pl-8'>
                                                    <h4 className='font-bold'>Timestamp</h4>
                                                    <p>{
                                                        new Date(item.timestamp).toLocaleString('th-TH', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                            hour: 'numeric',
                                                            minute: 'numeric'
                                                        })
                                                    }</p>
                                                </div>
                                                <div className='pb-4 pl-8'>
                                                    <h4 className='font-bold'>Request URL</h4>
                                                    <p>{process.env.REACT_APP_CLOUD_URL}{item.url}</p>
                                                </div>
                                                <div className='pb-4 pl-8'>
                                                    <h4 className='font-bold'>Filename</h4>
                                                    <p>{process.env.REACT_APP_CLOUD_URL}{item.filename}</p>
                                                </div>
                                                <div className='pb-4 pl-8'>
                                                    <h4 className='font-bold'>File size</h4>
                                                    <p>{convertFileSize(item.length)}</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )) : (
                                <tr>
                                    <td colSpan={4} className='p-4 pl-8 text-center'>ไม่พบข้อมูล</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {loading && <div className='w-full flex justify-center p-4'><CircularProgress /></div>}
                    {!hasMore && <p className='text-center p-4 border border-t-0'>No more files to load</p>}
                </div>
            </div>

            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={alertOpen} autoHideDuration={3000} onClose={() => setAlertOpen(false)}>
                <Alert onClose={() => setAlertOpen(false)} severity={alertText.type}>
                    {alertText.msg}
                </Alert>
            </Snackbar>
        </>
    );
}